/**
 * View Mode State Management
 *
 * Manages toggle between Comparisons and Responses views in /results.
 * Persists selection in URL hash for deep linking: #view=comparisons|responses
 */

import { useState, useEffect } from 'react';
import { logger } from '../../../lib/logging';

export type ViewMode = 'comparisons' | 'responses';

const DEFAULT_VIEW_MODE: ViewMode = 'comparisons';

/**
 * Get view mode from URL hash
 */
function getViewModeFromHash(): ViewMode {
  const hash = window.location.hash.slice(1); // Remove #
  const params = new URLSearchParams(hash);
  const view = params.get('view');

  if (view === 'comparisons' || view === 'responses') {
    return view;
  }

  return DEFAULT_VIEW_MODE;
}

/**
 * Set view mode in URL hash
 */
function setViewModeInHash(mode: ViewMode): void {
  const hash = window.location.hash.slice(1);
  const params = new URLSearchParams(hash);
  params.set('view', mode);
  window.location.hash = params.toString();
}

/**
 * React hook for view mode management
 */
export function useViewMode(): {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  isComparisonsView: boolean;
  isResponsesView: boolean;
} {
  const [viewMode, setViewModeState] = useState<ViewMode>(getViewModeFromHash);

  // Listen for hash changes (back/forward navigation)
  useEffect(() => {
    const handleHashChange = () => {
      const newMode = getViewModeFromHash();
      setViewModeState(newMode);

      logger.info('view_mode_restored', undefined, {
        op: 'view_mode_restored',
        component: 'viewMode',
        value: newMode,
        source: 'hash_change'
      });
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const setViewMode = (mode: ViewMode) => {
    const startTime = performance.now();

    setViewModeState(mode);
    setViewModeInHash(mode);

    logger.info('view_mode_set', undefined, {
      op: 'view_mode_set',
      component: 'viewMode',
      value: mode,
      duration_ms: performance.now() - startTime
    });
  };

  return {
    viewMode,
    setViewMode,
    isComparisonsView: viewMode === 'comparisons',
    isResponsesView: viewMode === 'responses'
  };
}
